/**
 * AddressReviewPanel (Packet G4)
 *
 * Surfaces the 7 structured sub-fields (`{field}_number/_street/_city/_state/_postal/
 * _country/_standardized`) that addressValidationService writes onto a parent field opted
 * into "Validate as structured address" (FieldConfigurationDialog). ValidationForm only
 * renders fields that have a FieldConfiguration entry, so these backend-generated,
 * config-less fields are otherwise invisible to a HITL reviewer even though they already
 * flow into exports (E2/E4).
 *
 * Read-only by design: the sub-fields are mechanically re-derived from the parent field's
 * value on every processing pass, so correction happens on the parent address, not here.
 * Their `confidence` (set by writeAddressSyntheticFields) is a 0/1 parse-success flag, not
 * an OCR confidence score — deliberately NOT run through ConfidenceIndicator/StatusBadge,
 * which would misreport it as a 0%/100% extraction confidence that was never measured.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ExtractedField } from '../../types';

/** Mirrors ADDRESS_SYNTHETIC_SUFFIXES in azure-functions/src/services/addressValidationService.ts. */
export const ADDRESS_SUB_FIELD_SUFFIXES = [
  '_number', '_street', '_city', '_state', '_postal', '_country', '_standardized'
] as const;

const SUB_FIELD_LABELS: Array<{ suffix: typeof ADDRESS_SUB_FIELD_SUFFIXES[number]; label: string }> = [
  { suffix: '_number', label: 'Street Number' },
  { suffix: '_street', label: 'Street' },
  { suffix: '_city', label: 'City' },
  { suffix: '_state', label: 'State' },
  { suffix: '_postal', label: 'Postal Code' },
  { suffix: '_country', label: 'Country' },
  { suffix: '_standardized', label: 'Standardized' },
];

export interface AddressFieldGroup {
  parentField: ExtractedField;
  subFields: Partial<Record<typeof ADDRESS_SUB_FIELD_SUFFIXES[number], ExtractedField>>;
}

interface AddressReviewPanelProps {
  addressFields: AddressFieldGroup[];
}

const AddressReviewPanel: React.FC<AddressReviewPanelProps> = ({ addressFields }) => {
  if (addressFields.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }} aria-label="Address review">
      <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Address Review
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Structured components parsed from the address field above. These are re-derived from
        that field, not independently extracted — correct the address field itself if a
        component below looks wrong.
      </Typography>

      <Stack spacing={2}>
        {addressFields.map(({ parentField, subFields }) => {
          const wasParsed = Object.values(subFields).some(f => f && f.confidence === 1);

          return (
            <Card variant="outlined" key={parentField.id}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
                    {parentField.displayName || parentField.name} — Structured Address
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={wasParsed ? 'success' : 'warning'}
                    icon={wasParsed ? <CheckCircleIcon fontSize="small" /> : <WarningAmberIcon fontSize="small" />}
                    label={wasParsed ? 'Parsed' : 'Could not parse'}
                  />
                  <Chip size="small" variant="outlined" label="Derived value — not an extraction confidence score" />
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                    gap: 2,
                  }}
                >
                  {SUB_FIELD_LABELS.map(({ suffix, label }) => {
                    const value = subFields[suffix]?.value ?? '';
                    return (
                      <Box key={suffix}>
                        <Typography
                          variant="caption"
                          component="div"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                          id={`address-subfield-label-${parentField.id}-${suffix}`}
                        >
                          {label}
                        </Typography>
                        <Typography
                          variant="body2"
                          aria-labelledby={`address-subfield-label-${parentField.id}-${suffix}`}
                          sx={{
                            fontStyle: value ? 'normal' : 'italic',
                            color: value ? 'text.primary' : 'text.disabled',
                          }}
                        >
                          {value || 'Not parsed'}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};

export default AddressReviewPanel;
